import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, articles } from '@/mocks/articles';
import arts from '@/mocks/zh/articles';

const articleImages: Record<string, string> = {
  'article-guide-01': 'https://readdy.ai/api/search-image?query=Abstract%20minimalist%20professional%20illustration%20with%20warm%20cream%20and%20golden%20tones%2C%20soft%20geometric%20patterns%2C%20clean%20modern%20corporate%20aesthetic%2C%20premium%20luxurious%20brand%20visual%2C%20subtle%20texture%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-guide-01&orientation=landscape',
  'article-security-01': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20shield%20and%20lock%20themed%20illustration%20with%20warm%20amber%20and%20cream%20colors%2C%20soft%20geometric%20shapes%2C%20clean%20corporate%20style%2C%20trustworthy%20and%20secure%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-security-01&orientation=landscape',
  'article-news-01': 'https://readdy.ai/api/search-image?query=Abstract%20geometric%20upgrade%20and%20innovation%20themed%20illustration%20with%20warm%20golden%20gradients%2C%20clean%20corporate%20style%2C%20modern%20and%20fresh%20aesthetic%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-news-01&orientation=landscape',
  'article-faq-01': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20question%20mark%20and%20dialogue%20themed%20illustration%20with%20soft%20warm%20tones%2C%20geometric%20clean%20design%2C%20friendly%20and%20helpful%20vibe%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-faq-01&orientation=landscape',
  'article-service-01': 'https://readdy.ai/api/search-image?query=Abstract%20minimalist%20headset%20and%20support%20themed%20illustration%2C%20warm%20amber%20and%20cream%20palette%2C%20soft%20geometric%20design%2C%20professional%20corporate%20style%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-service-01&orientation=landscape',
  'article-exp-01': 'https://readdy.ai/api/search-image?query=Abstract%20minimalist%20star%20and%20review%20themed%20illustration%20with%20warm%20golden%20tones%2C%20soft%20geometric%20patterns%2C%20positive%20and%20trustworthy%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-exp-01&orientation=landscape',
  'article-mobile-01': 'https://readdy.ai/api/search-image?query=Abstract%20minimalist%20mobile%20phone%20and%20app%20themed%20illustration%2C%20warm%20amber%20and%20cream%20gradients%2C%20geometric%20clean%20design%2C%20modern%20tech%20corporate%20style%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-mobile-01&orientation=landscape',
  'article-phishing-01': 'https://readdy.ai/api/search-image?query=Abstract%20minimalist%20warning%20and%20security%20themed%20illustration%20with%20warm%20golden%20and%20amber%20tones%2C%20geometric%20patterns%2C%20alert%20but%20calm%20mood%2C%20no%20text%2C%20no%20people&width=1200&height=600&seq=article-phishing-01&orientation=landscape',
};

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => getArticleBySlug(slug || ''), [slug]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />
        </div>
        <div className="w-full bg-background-50 py-20">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
              <i className="ri-file-unknow-line text-foreground-300 text-2xl" />
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">该文章不存在或已被移除</p>
            <Link to="/articles" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              ← 返回文章列表
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    return lines.map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={index} />;
      if (trimmed.startsWith('## ')) {
        return <h2 key={index} className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-4">{trimmed.slice(3)}</h2>;
      }
      if (trimmed.startsWith('### ')) {
        return <h3 key={index} className="font-heading text-lg font-semibold text-foreground-800 mt-6 mb-3">{trimmed.slice(4)}</h3>;
      }
      if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        return <p key={index} className="font-semibold text-foreground-800 mt-4 mb-2">{trimmed.slice(2, -2)}</p>;
      }
      if (trimmed.startsWith('- ')) {
        return <li key={index} className="text-foreground-600 text-sm leading-relaxed ml-4 list-disc">{trimmed.slice(2)}</li>;
      }
      if (trimmed.startsWith('1. ') || trimmed.match(/^\d+\. /)) {
        return <li key={index} className="text-foreground-600 text-sm leading-relaxed ml-4 list-decimal">{trimmed.replace(/^\d+\.\s*/, '')}</li>;
      }
      if (trimmed.startsWith('**Q:') || trimmed.startsWith('**Q：')) {
        return <p key={index} className="font-semibold text-foreground-800 mt-5 mb-1 text-sm">{trimmed.replace(/\*\*/g, '')}</p>;
      }
      if (trimmed.startsWith('A:') || trimmed.startsWith('A：')) {
        return <p key={index} className="text-foreground-600 text-sm leading-relaxed mb-4 ml-4">{trimmed}</p>;
      }
      return <p key={index} className="text-foreground-600 text-sm leading-relaxed mb-3">{trimmed}</p>;
    });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '文章', path: '/articles' },
            { label: article.title },
          ]}
        />
      </div>

      <main>
        <article className="w-full bg-background-50">
          <div className="relative w-full aspect-[21/9] max-h-[400px] overflow-hidden">
            <img
              src={articleImages[article.imageSeq]}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent" />
          </div>

          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 -mt-24 relative z-10">
            <div className="bg-background-50 rounded-xl p-6 md:p-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-400">{article.publishedAt}</span>
                <span className="text-xs text-foreground-300">·</span>
                <span className="text-xs text-foreground-400">{article.readCount.toLocaleString()} {arts.readCount}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-2 mb-8 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded bg-background-100 text-foreground-500 border border-background-200">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="prose max-w-none">
                {renderContent(article.content)}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full bg-background-100/70 py-12 md:py-16">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                {arts.relatedArticles}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/articles/${ra.slug}`}
                    className="group rounded-xl bg-background-50 overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={articleImages[ra.imageSeq]}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-1">{ra.publishedAt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <div className="w-full bg-background-50 py-8">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <Link
              to="/articles"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <i className="ri-arrow-left-line" /> 返回文章列表
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}